import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Text } from '../common';
import {
  getBusinessDates,
  isToday,
  getMonthName,
  checkHoliday
} from '@utils/date';
import CalendarHeader from './CalendarHeader';
import CalendarContainer from './CalendarContainer';
import CalendarButton from './CalendarButton';
import defaultTheme from '@styles/theme';
import useDetectSize from '@hooks/useDetectSize';

const TextWrapper = styled(Box)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  width: 104px;
  height: 48px;
  padding: 4px 8px;
  @media screen and (min-width: 768px) and (max-width: 1023px) {
    width: 80px;
    justify-content: center;
    align-items: center;
  }
  @media screen and (max-width: 767px) {
    width: 52px;
    justify-content: center;
    align-items: center;
  }
`;

const DayRow = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding-bottom: 0.5px;

  border-bottom: 1px solid rgb(216, 216, 216);

  &:last-child {
    border-bottom: 0;
  }

  ${props =>
    props.type === 'range' && {
      background: defaultTheme.colors.secondary
    }}
`;

function Calendar(props) {
  const {
    month,
    year,
    selectedItem,
    specialItems,
    highlightedRange,
    renderSpecialItem,
    onSelect,
    onPrev,
    onNext,
    renderHeader,
    renderFooter,
    enableSpecialItemsOnly,
    selectedExtractor
  } = props;
  const rows = getBusinessDates(month, year);
  const { serviceDate, serviceMonth } = selectedItem || {};
  const isMobile = useDetectSize();

  const days = isMobile
    ? ['M', 'T', 'W', 'T', 'F', 'S']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const renderRow = row => {
    const { days } = row;
    return days.map(momentDate => {
      const key = `calendar_btn_${momentDate.unix()}`;
      const dateNum = momentDate.date();
      const mon = momentDate.month() + 1;
      const title = `${dateNum}`;

      const week = momentDate.week();
      const isDateToday = isToday(momentDate);
      const holidayDate = checkHoliday(momentDate);
      const isDateHoliday = !!holidayDate;

      const inRange = highlightedRange && highlightedRange.weeks.includes(week);

      const specialItem =
        specialItems[mon] && specialItems[mon][dateNum]
          ? specialItems[mon][dateNum]
          : null;

      const isSelected =
        inRange || (mon === serviceMonth && dateNum === serviceDate);

      const isDisabled =
        !isSelected &&
        !isDateToday &&
        (isDateHoliday || momentDate.isBefore(moment()));

      return (
        <CalendarButton
          holiday={holidayDate}
          key={key}
          isToday={isDateToday && !selectedItem}
          selectedExtractor={selectedExtractor}
          title={title}
          date={momentDate}
          specialItem={specialItem}
          renderSpecialItem={renderSpecialItem}
          selected={isSelected}
          disabled={isDisabled}
          onSelect={onSelect}
          isSelectionDisabled={enableSpecialItemsOnly && !specialItem}
        />
      );
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flex={1}
      mb={4}
    >
      {renderHeader ? (
        renderHeader(getMonthName(month))
      ) : (
        <CalendarHeader
          title={getMonthName(month)}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}

      <DayRow>
        {days.map((day, index) => {
          const key = `calendar_days_${day}_${index}`;
          return (
            <TextWrapper key={key}>
              <Text fontSize={0}>{day}</Text>
            </TextWrapper>
          );
        })}
      </DayRow>

      <CalendarContainer
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        {rows.map(row => {
          const { week } = row;
          const key = `calendar_row_${month}_${week}`;
          const type =
            highlightedRange &&
            week >= highlightedRange.start &&
            week <= highlightedRange.end
              ? 'range'
              : undefined;

          return (
            <Row type={type} key={key}>
              {renderRow(row)}
            </Row>
          );
        })}
      </CalendarContainer>

      {renderFooter && renderFooter()}
    </Box>
  );
}

Calendar.propTypes = {
  month: PropTypes.number,
  year: PropTypes.number,
  selectedItem: PropTypes.object,
  specialItems: PropTypes.object,
  highlightedRange: PropTypes.object,
  renderSpecialItem: PropTypes.func,
  onSelect: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  renderHeader: PropTypes.func,
  renderFooter: PropTypes.func,
  selectedExtractor: PropTypes.func,
  enableSpecialItemsOnly: PropTypes.bool
};

export default Calendar;
